import { useState } from 'react';
import { imageUrl, type Guide } from '../model/steps';
import { stepsUsing } from '../model/edit';
import { linkify } from '../lib/linkify';

// A step's note, with any web address in it as a link that opens in a new tab.
function Note({ text }: { text: string }) {
  return <>{linkify(text).map((p, k) => (p.url
    ? <a key={k} href={p.url} target="_blank" rel="noopener noreferrer">{p.text}<span className="sr-only"> (opens in a new tab)</span></a>
    : <span key={k}>{p.text}</span>))}</>;
}
const hasLink = (text?: string) => !!text && linkify(text).some((p) => p.url);

// One guide as staff see it: the numbered steps on the left, the pictures on the right. Clicking a step shows its picture;
// clicking a picture in the row underneath shows it and marks the step it belongs to. On a phone the gallery is hidden and the
// chosen step's picture opens right under it.
export function GuideView({ guide }: { guide: Guide }) {
  const first = guide.steps.find((s) => s.image);
  const [step, setStep] = useState<string | undefined>(first?.id);
  const [shown, setShown] = useState<string | undefined>(first?.image ?? guide.images[0]?.id);

  const pickStep = (id: string, image?: string) => { setStep(id); if (image) setShown(image); };
  const pickImage = (id: string) => { setShown(id); const n = stepsUsing(guide, id)[0]; setStep(n ? guide.steps[n - 1]!.id : undefined); };

  const hasGallery = guide.images.length > 0;
  const shownAt = guide.images.findIndex((im) => im.id === shown);
  const big = guide.images[shownAt];
  const altOf = (k: number) => guide.images[k]!.caption || `Picture ${k + 1}`;
  const stepNo = (imageId: string) => stepsUsing(guide, imageId);
  const selected = guide.steps.find((s) => s.id === step);

  return (
    <div className={`help-layout${hasGallery ? ' with-gallery' : ''}`}>
      <div className="help-col">
        <h4 className="help-title">{guide.title}</h4>
        {guide.intro && <p className="help-intro">{guide.intro}</p>}
        {guide.link && (
          <p className="help-get">
            <a className="btn ghost sm" href={guide.link.url} target="_blank" rel="noopener noreferrer">{guide.link.label}<span className="sr-only"> (opens in a new tab)</span> ↗</a>
          </p>
        )}
        {hasGallery && <p className="hint help-tip">Click a step to see its picture.</p>}
        <ol className="help-steps">
          {guide.steps.map((st, i) => (
            <li key={st.id}>
              {hasGallery ? (
                <button type="button" className="help-step" aria-pressed={st.id === step} onClick={() => pickStep(st.id, st.image)}>
                  <span className="help-n" aria-hidden="true">{i + 1}</span>
                  <span className="help-body">
                    <span className="sr-only">Step {i + 1}: </span>
                    <span className="help-text">{st.text}</span>
                    {st.detail && !hasLink(st.detail) && <span className="hint help-detail">{st.detail}</span>}
                    {st.image && <span className="help-pic-tag">Picture {guide.images.findIndex((im) => im.id === st.image) + 1}</span>}
                  </span>
                </button>
              ) : (
                <div className="help-step static">
                  <span className="help-n" aria-hidden="true">{i + 1}</span>
                  <span className="help-body">
                    <span className="help-text">{st.text}</span>
                    {st.detail && <span className="hint help-detail help-note"><Note text={st.detail} /></span>}
                  </span>
                </div>
              )}
              {/* a note with a link sits under the step's button, since a link can't be inside a button */}
              {hasGallery && hasLink(st.detail) && <div className="hint help-detail help-note help-note-out"><Note text={st.detail!} /></div>}
              {st.id === step && st.image && (
                <img className="help-inline" src={imageUrl(st.image)} alt={`Picture for step ${i + 1}: ${guide.images.find((im) => im.id === st.image)?.caption || st.text}`} />
              )}
            </li>
          ))}
        </ol>
      </div>

      {hasGallery && (
        <aside className="help-gallery" aria-label="Pictures">
          <figure className="help-big">
            {big ? <img src={imageUrl(big.id)} alt={altOf(shownAt)} /> : <div className="help-none">No picture</div>}
            <figcaption>
              {selected && !selected.image && step
                ? <>Step {guide.steps.indexOf(selected) + 1} has no picture. </>
                : null}
              {big && <><b>Picture {shownAt + 1} of {guide.images.length}</b>{big.caption && <> · {big.caption}</>}{stepNo(big.id).length > 0 && <> · step {stepNo(big.id).join(', ')}</>}</>}
            </figcaption>
          </figure>
          <div className="help-thumbs" role="group" aria-label="All pictures">
            {guide.images.map((im, k) => (
              <button key={im.id} type="button" className="help-thumb" aria-pressed={im.id === shown} aria-label={`Picture ${k + 1}${im.caption ? `: ${im.caption}` : ''}`} onClick={() => pickImage(im.id)}>
                <img src={imageUrl(im.id)} alt="" loading="lazy" />
                <span aria-hidden="true">{k + 1}</span>
              </button>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
}
